import { useState, useMemo, Fragment } from 'react';
import { Search, Landmark, ChevronRight, ChevronDown, FileText, Receipt, SearchX } from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { StatusBadge } from '@/components/shared/status-badge';
import { DateInput } from '@/components/ui/date-input';
import { useRPFundingSummary } from '@/hooks/use-receive-payments';
import { FUNDED_SALE_STATUSES, useFundingRefunds, useSaleTransactions } from '@/hooks/use-sale-transactions';
import { formatCurrency, formatDateTime } from '@/lib/utils';

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// ── Expanded transactions panel ──

function FundingTransactionsPanel({
  fundingDate,
  paymentMethod,
  currency,
}: {
  fundingDate: string;
  paymentMethod: string;
  currency: string;
}) {
  // A sale that was later (partially) refunded still counts at full amount on its funding day,
  // so the statuses include the refund ones; the refunds themselves come in as negative lines.
  const { data: salesData, isLoading: salesLoading } = useSaleTransactions({
    status: FUNDED_SALE_STATUSES.join(','),
    postDate: fundingDate,
    paymentMethod,
    perPage: 1000,
  });
  const { data: refundsData, isLoading: refundsLoading } = useFundingRefunds(fundingDate, paymentMethod);
  const isLoading = salesLoading || refundsLoading;

  const transactions = useMemo(() => {
    const pm = paymentMethod.toLowerCase();
    const cur = currency.toLowerCase();

    const sales = (salesData?.data ?? [])
      .filter(
        (s) =>
          s.postDate?.startsWith(fundingDate) &&
          FUNDED_SALE_STATUSES.includes(s.status.toLowerCase()) &&
          s.paymentMethod.toLowerCase() === pm &&
          s.currency.toLowerCase() === cur,
      )
      .map((s) => ({
        id: s.id,
        referenceNumber: s.referenceNumber,
        customerName: s.firstName && s.lastName ? `${s.firstName} ${s.lastName}` : s.cardHolderName || 'Direct Sale',
        invoiceNumber: '—',
        amount: s.amount,
        isRefund: false,
        paymentMethod: s.paymentMethod,
        status: s.status,
        date: s.createdAt,
        hasAuthDoc: s.hasAuthDoc ?? false,
        sourceType: s.sourceType ?? null,
      }));

    const refunds = (refundsData?.data ?? [])
      .filter((r) => r.currency.toLowerCase() === cur)
      .map((r) => ({
        id: `refund-${r.id}`,
        referenceNumber: r.referenceNumber,
        customerName: r.firstName && r.lastName ? `${r.firstName} ${r.lastName}` : 'Direct Sale',
        invoiceNumber: '—',
        amount: r.amount,
        isRefund: true,
        paymentMethod: r.paymentMethod,
        status: r.status,
        date: r.createdAt,
        hasAuthDoc: false,
        sourceType: null as string | null,
      }));

    return [...sales, ...refunds];
  }, [salesData, refundsData, fundingDate, paymentMethod, currency]);

  const handleViewAuthDoc = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    window.open(`/rp-api/v1/ach-authorizations/signed-document?saleId=${encodeURIComponent(id)}`, '_blank');
  };

  if (isLoading) {
    return <div className="py-6 text-center text-sm text-muted-foreground">Loading transactions...</div>;
  }

  if (transactions.length === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="No transactions found"
        description="No completed transactions or refunds were posted for this funding date."
        className="py-8"
      />
    );
  }

  return (
    <div className="rounded-lg border border-border bg-white overflow-x-auto">
      <table className="min-w-[700px] w-full">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Reference
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Customer
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Invoice
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-right text-xs font-medium text-muted-foreground whitespace-nowrap">
              Amount
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Method
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Source
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Status
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Auth Doc
            </th>
            <th className="px-3 sm:px-4 py-2.5 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
              Date
            </th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((txn) => (
            <tr key={txn.id} className="border-b border-border last:border-0 hover:bg-muted/30">
              <td className="px-3 sm:px-4 py-2.5">
                <span className="font-mono text-xs font-medium text-primary whitespace-nowrap">
                  {txn.referenceNumber}
                </span>
              </td>
              <td className="px-3 sm:px-4 py-2.5 text-sm whitespace-nowrap">{txn.customerName}</td>
              <td className="px-3 sm:px-4 py-2.5 text-sm whitespace-nowrap">{txn.invoiceNumber}</td>
              <td
                className={`px-3 sm:px-4 py-2.5 text-sm font-medium text-right whitespace-nowrap ${
                  txn.isRefund ? 'text-red-600' : ''
                }`}
              >
                {txn.isRefund ? `-${formatCurrency(txn.amount)}` : formatCurrency(txn.amount)}
              </td>
              <td className="px-3 sm:px-4 py-2.5">
                <span className="uppercase text-xs font-medium whitespace-nowrap">{txn.paymentMethod}</span>
              </td>
              <td className="px-3 sm:px-4 py-2.5 whitespace-nowrap">
                {txn.isRefund ? (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                    Refund
                  </span>
                ) : txn.sourceType ? (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      txn.sourceType === 'pay-by-link'
                        ? 'bg-purple-100 text-purple-700'
                        : txn.sourceType === 'pay-by-link-recurring'
                          ? 'bg-violet-100 text-violet-700'
                          : txn.sourceType === 'virtual-recurring'
                            ? 'bg-teal-100 text-teal-700'
                            : txn.sourceType === 'virtual-scheduled'
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                    }`}
                  >
                    {txn.sourceType === 'pay-by-link'
                      ? 'Pay By Link'
                      : txn.sourceType === 'pay-by-link-recurring'
                        ? 'Pay By Link-Recurring'
                        : txn.sourceType === 'virtual-recurring'
                          ? 'Virtual-Recurring'
                          : txn.sourceType === 'virtual-scheduled'
                            ? 'Virtual-Scheduled'
                            : 'Virtual'}
                  </span>
                ) : (
                  <span className="text-muted-foreground text-sm">—</span>
                )}
              </td>
              <td className="px-3 sm:px-4 py-2.5">
                <StatusBadge
                  status={txn.status}
                  labelMap={txn.isRefund ? { completed: 'Refund settled' } : undefined}
                />
              </td>
              <td className="px-3 sm:px-4 py-2.5">
                {txn.hasAuthDoc ? (
                  <button
                    onClick={(e) => handleViewAuthDoc(e, txn.id)}
                    className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    View
                  </button>
                ) : (
                  <span className="text-muted-foreground text-sm">—</span>
                )}
              </td>
              <td className="px-3 sm:px-4 py-2.5 text-sm text-muted-foreground whitespace-nowrap">
                {formatDateTime(txn.date)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main tab ──

export function FundingSummaryTab() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  const { data, isLoading } = useRPFundingSummary({
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
    ...(paymentMethod && { paymentMethod }),
  });

  const entries = data?.data ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.funding_date.includes(q) ||
        e.payment_method.toLowerCase().includes(q) ||
        e.currency.toLowerCase().includes(q),
    );
  }, [entries, search]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => filtered.slice((page - 1) * perPage, page * perPage), [filtered, page, perPage]);

  const toggleRow = (key: string) => setExpandedRow((prev) => (prev === key ? null : key));

  return (
    <div className="min-w-0 space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by date, method or currency..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <select
          value={paymentMethod}
          onChange={(e) => {
            setPaymentMethod(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
        >
          <option value="">All Methods</option>
          <option value="ach">ACH</option>
          <option value="card">Card</option>
        </select>

        <div className="flex w-40 flex-col gap-0.5">
          <label className="text-xs text-muted-foreground pl-1">Start Date</label>
          <DateInput
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex w-40 flex-col gap-0.5">
          <label className="text-xs text-muted-foreground pl-1">End Date</label>
          <DateInput
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        {(startDate || endDate || paymentMethod) && (
          <button
            type="button"
            onClick={() => {
              setStartDate('');
              setEndDate('');
              setPaymentMethod('');
              setPage(1);
            }}
            className="h-9 rounded-lg border border-input bg-background px-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading funding summary...</div>
      ) : filtered.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          {search ? (
            <EmptyState
              icon={SearchX}
              title="No matching funding entries"
              description="Try a different search term or clear the filters."
            />
          ) : (
            <EmptyState
              icon={Landmark}
              title="No funding summary yet"
              description="Settled deposits will appear here once funding data is available."
            />
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-x-auto">
          <table style={{ width: '100%', tableLayout: 'fixed' }}>
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th style={{ width: '40px' }} className="px-3 py-3" aria-label="Expand" />
                <th
                  style={{ width: '22%' }}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap"
                >
                  Funding Date
                </th>
                <th
                  style={{ width: '22%' }}
                  className="px-4 py-3 text-right text-xs font-medium text-muted-foreground whitespace-nowrap"
                >
                  Total Amount
                </th>
                <th
                  style={{ width: '22%' }}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap"
                >
                  Payment Method
                </th>
                <th
                  style={{ width: '18%' }}
                  className="px-4 py-3 text-right text-xs font-medium text-muted-foreground whitespace-nowrap"
                >
                  Transactions Count
                </th>
                <th
                  style={{ width: '16%' }}
                  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap"
                >
                  Currency
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((entry, i) => {
                const rowKey = `${entry.funding_date}-${entry.payment_method}-${i}`;
                const isExpanded = expandedRow === rowKey;
                return (
                  <Fragment key={rowKey}>
                    <tr
                      className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer"
                      onClick={() => toggleRow(rowKey)}
                    >
                      <td className="px-3 py-3">
                        <button
                          type="button"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                        {entry.funding_date}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-right whitespace-nowrap">
                        {formatCurrency(entry.total_amount)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="uppercase text-xs font-semibold tracking-wide text-foreground">
                          {entry.payment_method}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-right">{entry.transaction_count}</td>
                      <td className="px-4 py-3 text-xs font-medium text-muted-foreground">{entry.currency}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-border bg-muted/10">
                        <td colSpan={6} className="px-6 py-4">
                          <FundingTransactionsPanel
                            fundingDate={entry.funding_date}
                            paymentMethod={entry.payment_method}
                            currency={entry.currency}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && total > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}
